import { BLUE_10, BLUE_50, GREY_20, RED_40, GREY_60, BLUE_60, GREY_50, BLUE_30 } from '@app/lib/colors';
import { FleetCards } from '@app/lib/i18n-backend-strings';

import { ICardMetadata, IPreviouslyAssignedTo, IServerCardMetadata } from '../card-management-v2/card-management.interface';

export enum AccountStatus {
  OK = 'ok',
  DUE = 'due',
  WARN = 'warn',
  CREDIT_FROZEN = 'credit_frozen',
}

export enum CcmdStatus {
  LOCKED = 'account_locked',
  CREDIT_FROZEN = 'credit_frozen',
  CANCELED = 'canceled',
  DISQUALIFIED = 'disqualified',
}

export enum PlaidReauthNoticeStatus {
  SYNCED = 'synced',
  NOTIFIED = 'notified',
  ELAPSED = 'elapsed',
}


export enum AllowedCategory {
  FUEL_ONLY = 'Fuel Only',
  FUEL_AND_FLEET_EXPENSES_ONLY = 'Fuel and Fleet Expenses Only',
  ALL_CATEGORIES = 'All Categories',
}

export enum AuthStatus {
  LOGIN_REQUIRED = 'login_required',
  AUTHED = 'authed',
  PENDING_EXPIRATION = 'pending_expiration',
  MIRCO_DEPOSIT = 'micro_deposit',
}

export enum RISK_PROFILE {
  MEDIUM = 'medium_risk',
  LOW = 'low_risk',
  HIGH = 'high_risk',
}

export enum OFFBOARD_METADATA {
  INACTIVITY_AUTO = 'inactivty_auto_terminated',
  INACTIVITY = 'inactivity',
  COMPANY_REQUEST = 'company_request',
}

export const DISQUALIFIED_REASON = [
  'past_due',
  'payment_history',
  'bankruptcy',
  'credit_bureau_related',
  'abuse_of_card_items',
  'return_payment',
  'bank_does_not_support',
  'financials_does_not_support',
  'out_of_business',
  'portfolio_management_cancelation',
  'deceased',
];

export const CCMD_STATUSES = Object.values(CcmdStatus);

export const OVERVIEW_METADATA_OK_V2 = [
  {
    label: FleetCards.accountOverviewCurrentBalance,
    color: BLUE_50,
    fillColor: BLUE_50,
    popover: FleetCards.accountOverviewCurrentBalancePopoverV2,
  },
  {
    label: FleetCards.accountOverviewPendingTransactions,
    color: GREY_20,
    fillColor: GREY_20,
    popover: FleetCards.accountOverviewPendingTransactionsPopoverV2,
  },
  {
    label: FleetCards.accountOverviewPaymentsInProcess,
    color: GREY_60,
    fillColor: GREY_60,
    popover: FleetCards.accountOverviewPaymentsInProcessPopover,
  },
  {
    label: FleetCards.accountOverviewRemainingCredit,
    color: BLUE_10,
    fillColor: BLUE_10,
    popover: FleetCards.accountOverviewRemainingCreditPopoverV2,
  },
];

export const OVERVIEW_METADATA_V3 = [
  {
    label: FleetCards.accountOverviewCurrentBalance,
    color: BLUE_60,
    fillColor: BLUE_60,
    popover: FleetCards.accountOverviewCurrentBalancePopoverV2,
  },
  {
    label: FleetCards.accountOverviewPaymentsInProcess,
    color: GREY_50,
    fillColor: GREY_50,
    popover: FleetCards.accountOverviewPaymentsInProcessPopover,
  },
  {
    label: FleetCards.accountOverviewPendingTransactions,
    color: GREY_20,
    fillColor: GREY_20,
    popover: FleetCards.accountOverviewPendingTransactionsPopoverV2,
  },
  {
    label: FleetCards.accountOverviewRemainingCredit,
    color: BLUE_30,
    fillColor: BLUE_30,
    popover: FleetCards.accountOverviewRemainingCreditPopoverV2,
  },
];

export const OVERVIEW_METADATA_CREDIT_FROZEN_V2 = [
  {
    label: FleetCards.accountOverviewCurrentBalance,
    color: BLUE_50,
    fillColor: BLUE_50,
    popover: FleetCards.accountOverviewCurrentBalancePopoverV2,
  },
  {
    label: FleetCards.accountOverviewPendingTransactions,
    color: GREY_20,
    fillColor: GREY_20,
    popover: FleetCards.accountOverviewPendingTransactionsPopoverV2,
  },
  {
    label: FleetCards.accountOverviewPaymentsInProcess,
    color: GREY_60,
    fillColor: GREY_60,
    popover: FleetCards.accountOverviewPaymentsInProcessPopover,
  },
  {
    label: FleetCards.accountOverviewRemainingCredit,
    color: RED_40,
    fillColor: RED_40,
    popover: FleetCards.accountOverviewRemainingCreditPopoverV2,
  },
];

export const OVERVIEW_METADATA_NO_ACCOUNT_LINKED_V2 = OVERVIEW_METADATA_OK_V2;

export enum PaymentMethodStatus {
  INITIATED = 'initiated',
  ACTIVE = 'active',
  PENDING_VERIFICATION = 'pending',
  RESTRICTED = 'restricted',
}

export enum CompanyCardMetaDataStatus {
  WAITLISTED = 'waitlisted',
  ACTIVE = 'active',
  CREDIT_APPROVED = 'credit_approved',
  APPLICATION_SUBMITTED = 'application_submitted',
  APPLICATION_DECLINED = 'application_declined',
  IN_APP_VERIFY_EMAIL = 'application_under_verification',
  PUBLIC_APP_VERIFY_EMAIL = 'public_application_under_verification',
}

export enum CustomerFleetSizeSegment {
  SMB = 'smb',
  MID_MARKET = 'mid_market',
  COMMERCIAL = 'commercial',
  ENTERPRISE = 'enterprise',
}

export interface IToggleOption {
  textCaps: string;
  text: string;
  value: string;
}

export interface IUnlockCard {
  lastFourDigits?: string;
  id: string;
  derivedStatus?: string;
  entityName?: string;
  entityType?: string;
  entityId?: number;
  metadata?: ICardMetadata[];
  previouslyAssignedTo?: IPreviouslyAssignedTo;
}

export interface IUnlockCardResponse {
  unlocked_till: string;
  metadata: IServerCardMetadata[];
}

export interface IServerUnlockCardUpdateEvent {
  unlockedTill: string;
  metadata: IServerCardMetadata[];
}

export interface IUnlockCardUpdateEvent {
  unlockedTill: string;
  metadata: ICardMetadata[];
}

export const BillingTabOptions: IToggleOption[] = [
  { text: $localize`Invoices`, textCaps: $localize`INVOICES`, value: 'invoices' },
  { text: $localize`Payments`, textCaps: $localize`PAYMENTS`, value: 'payments' },
];

export enum CARD_SECURITY_SETTINGS {
  vehicleProximityBasedDecline = 'vehicleProximityBasedDecline',
}

export enum COMPARISON_TOOLTIP_TYPE {
  SINGLE_KEY_VALUE = 'single_key_value',
  MULTIPLE_KEY_VALUES_WITH_TOGGLE = 'multiple_key_values_with_toggle',
  TOGGLE = 'toggle',
}